import React, { useState, useEffect, useCallback, useRef } from 'react';
import { invoicesService, expensesService, bankAccountsService } from '../services/api';
import { lerSecao, useEstadoDaCarga } from '../design';
import { fmtDateTime } from '../utils/formatters';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { contornoDoSinal, contornoDoSaldoDeConta } from './shared/lista/PecasDaLista';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import PeriodSelector, { PeriodFilter, defaultPeriodFilter, periodToParams } from './PeriodSelector';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface Props {
  contracts: any[];
  clients: any[];
  onNavigate?: (section: string) => void;
  /** R7: a carga principal do App (assinaturas) já leu. Sem ela, o MRR é "—", não R$ 0,00. Ausente = leu (compatível). */
  contratosLidos?: boolean;
}

const FinancialDashboardTab: React.FC<Props> = ({ contracts, clients, onNavigate, contratosLidos = true }) => {
  const now0 = new Date();
  const currentMonth = `${now0.getFullYear()}-${String(now0.getMonth() + 1).padStart(2, '0')}`;

  const [invoices, setInvoices] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [expSummary, setExpSummary] = useState<any>(null);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [cashFlowData, setCashFlowData] = useState<any[]>([]);
  // O faturamento por segmento NÃO CARREGOU (08/10/2026): antes caía calado em null e o card sumia sem dizer nada.
  const [segmentErro, setSegmentErro] = useState<string | null>(null);
  const [segmentData, setSegmentData] = useState<{ segments: { segment: string; total: number; clients: number; invoices: number }[]; total: number } | null>(null);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>(defaultPeriodFilter());

  /**
   * R1 a R4 (12/10/2026, medição do bloco F; padrão liberado pelo Maestro). A carga é ESTADO:
   * - R1: o Promise.all que falhava era só console.error, e o painel afirmava R$ 0,00, 0,0%, "Superávit", "Nenhuma
   *   fatura vencida". Sem leitura, os números são "—" e a faixa diz que não leu, com Tentar de novo.
   * - R2: os números são do RECORTE que leu. Trocar de mês e a leitura falhar deixava os do mês anterior sob o rótulo
   *   do novo; agora o recorte da tela diferente do lido = "—".
   * - R3: só a leitura mais recente grava (a resposta do mês velho que chega depois é descartada).
   * - R4: o "Carregando…" de tela inteira só na primeira carga; depois, o painel fica e o cabeçalho diz que está lendo.
   * A recarga do MESMO recorte que falha mantém os números e diz de quando são.
   */
  const recorte = JSON.stringify(periodToParams(periodFilter));
  const carga = useEstadoDaCarga();
  const [lidoDoRecorte, setLidoDoRecorte] = useState<{ recorte: string; em: string } | null>(null);
  const ultimaLeitura = useRef(0);

  const ler = useCallback(() => {
    const minha = ++ultimaLeitura.current;
    const chave = JSON.stringify(periodToParams(periodFilter));
    return carga.rodar(async () => {
        const p = periodToParams(periodFilter);
        const monthParam = p.month || periodFilter.months[0] || currentMonth;
        // Range de meses (YYYY-MM) pro faturamento por segmento (competência).
        const monthsSorted = [...(periodFilter.months || [])].sort();
        const segStart = monthsSorted[0] || monthParam;
        const segEnd = monthsSorted[monthsSorted.length - 1] || monthParam;
        const [inv, sum, expSum, banks, cf, seg] = await Promise.all([
          invoicesService.getByMonth(monthParam, p.startDate, p.endDate),
          invoicesService.getReceivablesSummary(p.month, p.startDate, p.endDate),
          expensesService.summary(p.month, p.startDate, p.endDate),
          bankAccountsService.getAll(),
          expensesService.cashFlow(6),
          lerSecao(expensesService.getRevenueBySegment(segStart, segEnd)),
        ]);
        if (minha !== ultimaLeitura.current) return; // resposta velha: a mais nova é que decide
        setInvoices(Array.isArray(inv) ? inv : []);
        setSummary(sum);
        setExpSummary(expSum);
        setBankAccounts(Array.isArray(banks) ? banks : []);
        setCashFlowData(cf?.months || []);
        setSegmentErro(seg.erro);
        setSegmentData(seg.valor && Array.isArray((seg.valor as any).segments) ? (seg.valor as any) : null);
        setLidoDoRecorte({ recorte: chave, em: new Date().toISOString() });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter]);
  useEffect(() => { void ler(); }, [ler]);

  const lido = lidoDoRecorte !== null && lidoDoRecorte.recorte === recorte;
  const lendo = carga.estado === 'carregando' || (!lido && carga.estado !== 'erro');
  /** Valor do recorte lido, ou "—": nunca o número de outro mês, nunca R$ 0,00 sem leitura. */
  const reais = (x: number) => (lido ? `R$ ${fmt(x)}` : '—');

  if (lidoDoRecorte === null && carga.estado !== 'erro') {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400"></div>
        <span className="ml-3 text-slate-400">Carregando resumo financeiro...</span>
      </div>
    );
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  // KPI calculations
  const activeContracts = contracts.filter((c: any) => c.status === 'Ativo');
  const mrrTotal = activeContracts.reduce((sum: number, c: any) => sum + (parseFloat(c.mrr) || 0), 0);

  const selectedMonth = periodFilter.months[0] || currentMonth;

  // invoices already filtered by selectedMonth via getByMonth API
  //
  // Cancelada e Rascunho NÃO são faturamento: a primeira foi anulada e a segunda
  // ainda não foi emitida. Somá-las inflava o KPI e, pior, inflava o DENOMINADOR
  // da inadimplência — que aparecia menor do que é. É o mesmo filtro que o DRE
  // usa (`NOT IN (Cancelada, Rascunho)`) e que a tela de Faturas já aplicava.
  const faturaveis = invoices.filter(
    (i: any) => i.status !== 'Cancelada' && i.status !== 'Rascunho',
  );
  const faturamentoMes = faturaveis.reduce((sum: number, i: any) => sum + (parseFloat(i.totalValue) || 0), 0);

  // "Em aberto" inclui Parcial: fatura parcialmente paga continua devendo o
  // saldo. Antes só 'Aberta' contava, então uma vencida com pagamento parcial
  // sumia da inadimplência do dashboard e aparecia como vencida na tela de
  // Faturas — dois números para a mesma pergunta.
  const emAberto = (i: any) => i.status === 'Aberta' || i.status === 'Parcial';
  // O que ainda se deve, não o valor cheio da fatura.
  const saldoDevedor = (i: any) =>
    Math.max((parseFloat(i.totalValue) || 0) - (parseFloat(i.paidValue) || 0), 0);
  const venceuAntesDeHoje = (i: any) => {
    if (!i.dueDate) return false;
    const [y, m, d] = i.dueDate.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d) < now;
  };

  const openInvoices = faturaveis.filter(emAberto);
  const overdueInvoices = openInvoices.filter(venceuAntesDeHoje);
  // Inadimplência: saldo vencido do mês / faturado no mês
  const monthOverdueInvoices = overdueInvoices;
  const totalMonthOverdue = monthOverdueInvoices.reduce((sum: number, i: any) => sum + saldoDevedor(i), 0);
  const inadimplencia = faturamentoMes > 0 ? (totalMonthOverdue / faturamentoMes) * 100 : 0;

  const totalReceived = summary?.totals?.totalReceived ?? 0;

  // Expense stats
  const expByStatus = expSummary?.byStatus || [];
  const expTotalMes = expByStatus.reduce((sum: number, s: any) => sum + (parseFloat(s.total) || 0), 0);
  const expPaid = expByStatus.filter((s: any) => s.status === 'Pago').reduce((sum: number, s: any) => sum + (parseFloat(s.total) || 0), 0);
  const expPending = expByStatus.filter((s: any) => s.status === 'Pendente' || s.status === 'Parcial').reduce((sum: number, s: any) => sum + (parseFloat(s.pendingTotal ?? s.total) || 0), 0);
  const expOverdue = expByStatus.filter((s: any) => s.status === 'Vencido').reduce((sum: number, s: any) => sum + (parseFloat(s.pendingTotal ?? s.total) || 0), 0);
  const expPendingTotal = expPending + expOverdue;

  // Resultado do Mês (Recebido - Despesas Pagas = fluxo de caixa real)
  const resultadoMes = totalReceived - expPaid;

  // Bank account balances
  const totalBankBalance = bankAccounts.filter((b: any) => b.isActive !== false).reduce((sum: number, b: any) => sum + (parseFloat(b.currentBalance) || 0), 0);

  // Top 5 categories by expense
  const expByCategory = expSummary?.byCategory || [];
  const categoryTotals: Record<string, { name: string; color: string; total: number }> = {};
  expByCategory.forEach((row: any) => {
    const key = row.categoryName || 'Sem categoria';
    if (!categoryTotals[key]) {
      categoryTotals[key] = { name: key, color: row.categoryColor || '#94a3b8', total: 0 }; // sem cor cadastrada: neutro, não o laranja velho
    }
    categoryTotals[key].total += parseFloat(row.total) || 0;
  });
  const topCategories = Object.values(categoryTotals).sort((a, b) => b.total - a.total).slice(0, 5);
  const maxCategoryTotal = topCategories[0]?.total || 1;

  // Boleto stats
  // Minúsculo: é assim que o banco grava. Com 'Boleto' esta lista vinha sempre
  // vazia e a seção de boletos do dashboard não mostrava nada.
  const boletoInvoices = openInvoices.filter(
    (i: any) => String(i.paymentMethod || '').toLowerCase() === 'boleto',
  );
  const boletosRegistered = boletoInvoices.filter((i: any) => i.nossoNumero).length;
  const boletosPending = boletoInvoices.filter((i: any) => !i.nossoNumero).length;
  const boletosPaid = invoices.filter((i: any) => {
    // R5 (12/10/2026): comparava com 'Boleto' maiúsculo, e o banco grava minúsculo (ver acima): o número era SEMPRE 0.
    if (String(i.paymentMethod || '').toLowerCase() !== 'boleto' || i.status !== 'Paga' || !i.paymentDate) return false;
    const pd = i.paymentDate.split('T')[0];
    return pd >= `${selectedMonth}-01` && pd <= `${selectedMonth}-31`;
  }).length;

  // Top 5 inadimplentes
  const overdueByClient: Record<number, { name: string; total: number; count: number }> = {};
  overdueInvoices.forEach((inv: any) => {
    const cid = inv.clientId;
    if (!overdueByClient[cid]) {
      const cl = clients.find((c: any) => c.id === cid);
      overdueByClient[cid] = { name: nomeDeExibicao(cl, `Cliente #${cid}`), total: 0, count: 0 };
    }
    // Saldo devedor, não o valor cheio: cliente que pagou metade de uma fatura
    // vencida aparecia devendo o dobro do que deve.
    overdueByClient[cid].total += saldoDevedor(inv);
    overdueByClient[cid].count += 1;
  });
  const topInadimplentes = Object.values(overdueByClient).sort((a, b) => b.total - a.total).slice(0, 5);
  const maxInadimplente = topInadimplentes[0]?.total || 1;

  return (
    <div className="space-y-6">
      {carga.estado === 'erro' && (
        <div role="alert" className={`px-4 py-3 rounded-xl border text-sm flex items-center justify-between gap-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-resumo-faixa={lido ? 'desatualizado' : 'sem-leitura'}>
          <span>
            {lido && lidoDoRecorte
              ? `Não consegui atualizar o resumo (${carga.erro}). Mostrando os números lidos em ${fmtDateTime(lidoDoRecorte.em)}.`
              : `Não consegui ler o resumo deste período (${carga.erro}). Os números ficam em "—" até ler.`}
          </span>
          <button type="button" onClick={() => void ler()} className="shrink-0 underline font-medium">Tentar de novo</button>
        </div>
      )}

      {/* Month Selector */}
      <div className="flex justify-end items-center gap-3">
        {lendo && lidoDoRecorte !== null && <span className="text-xs text-slate-400" role="status">Lendo o período…</span>}
        <PeriodSelector
          value={periodFilter}
          onChange={setPeriodFilter}
          singleMonth={true}
          showAllMonths={false}
        />
      </div>

      {/* Row 1: 6 KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* MRR Total */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <span className="text-slate-400 text-sm font-medium">MRR Total</span>
          </div>
          <p className="text-2xl font-bold text-white">{contratosLidos ? `R$ ${fmt(mrrTotal)}` : '—'}</p>
          <p className="text-xs text-slate-500 mt-1">{contratosLidos ? `${activeContracts.length} assinaturas ativas` : 'assinaturas não lidas'}</p>
        </div>

        {/* Faturamento do Mês */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
            <span className="text-slate-400 text-sm font-medium">Faturamento do Mês</span>
          </div>
          <p className="text-2xl font-bold text-white">{reais(faturamentoMes)}</p>
          <p className="text-xs text-slate-500 mt-1">{lido ? `${invoices.length} faturas em ${selectedMonth}` : 'sem leitura deste período'}</p>
          {lido && cashFlowData.length > 1 && (
            // R8: a curva é a receita dos ÚLTIMOS 6 MESES ATÉ HOJE, qualquer que seja o período escolhido. Agora diz.
            <div className="mt-2 -mx-1" title="Receita dos últimos 6 meses até hoje (não segue o período escolhido)">
              <ResponsiveContainer width="100%" height={40}>
                <AreaChart data={cashFlowData.map((m: any) => ({ v: parseFloat(m.revenue) || 0 }))}>
                  <Area type="monotone" dataKey="v" stroke="#60a5fa" fill="#60a5fa" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Inadimplência */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <span className="text-slate-400 text-sm font-medium">Inadimplência</span>
          </div>
          <p className="text-2xl font-bold text-white">{lido ? `${inadimplencia.toFixed(1)}%` : '—'}</p>
          <p className="text-xs text-slate-500 mt-1">{lido ? `R$ ${fmt(totalMonthOverdue)} vencido de R$ ${fmt(faturamentoMes)}` : 'sem leitura deste período'}</p>
        </div>

        {/* Recebido no Mês */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <span className="text-slate-400 text-sm font-medium">Recebido no Mês</span>
          </div>
          <p className="text-2xl font-bold text-white">{reais(totalReceived)}</p>
          <p className="text-xs text-slate-500 mt-1">Pagamentos confirmados</p>
        </div>

        {/* Despesas do Mês */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            </div>
            <span className="text-slate-400 text-sm font-medium">Despesas do Mês</span>
          </div>
          <p className="text-2xl font-bold text-white">{reais(expTotalMes)}</p>
          <p className="text-xs text-slate-500 mt-1">
            {!lido ? 'sem leitura deste período' : expOverdue > 0
              ? `R$ ${fmt(expPendingTotal)} pendente (R$ ${fmt(expOverdue)} vencido)`
              : expPendingTotal > 0
                ? `R$ ${fmt(expPendingTotal)} pendente · R$ ${fmt(expPaid)} pago`
                : `R$ ${fmt(expPaid)} pago`}
          </p>
        </div>

        {/* Saldo de Contas. K2: negativo é a exceção, com barra danger; o número fica neutro, com o sinal. */}
        <div className={`bg-slate-800/50 rounded-xl border border-slate-700 ${contornoDoSaldoDeConta(totalBankBalance)} p-5`} data-cartao-saldo>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
            </div>
            <span className="text-slate-400 text-sm font-medium">Saldo de Contas</span>
          </div>
          <p className="text-2xl font-bold text-white">{reais(totalBankBalance)}</p>
          <p className="text-xs text-slate-500 mt-1">{lido ? `${bankAccounts.filter(b => b.isActive !== false).length} contas ativas` : 'contas não lidas'}</p>
        </div>
      </div>

      {/* Row 2: 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Resultado do Mês. O contorno pelo sinal (S1, Heitor 23/10/2026): o resultado alterna, então a barra é verde no
            superávit e vermelha no déficit; a palavra "Déficit" segue no tom. Sem leitura, sem barra. */}
        <div className={`bg-slate-800/50 rounded-xl border border-slate-700 ${lido ? contornoDoSinal(resultadoMes) : ''} p-5`} data-cartao-resultado>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            </div>
            <span className="text-white font-semibold">Resultado do Mês</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xl font-bold text-white">{reais(totalReceived)}</p>
              <p className="text-xs text-slate-500">Recebido</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">{reais(expPaid)}</p>
              <p className="text-xs text-slate-500">Despesas Pagas</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">
                {lido ? `${resultadoMes >= 0 ? '+' : ''}R$ ${fmt(resultadoMes)}` : '—'}
              </p>
              <p className={`text-xs ${lido && resultadoMes < 0 ? `${TONE_TEXT.danger} font-semibold` : 'text-slate-500'}`}>{!lido ? 'sem leitura' : resultadoMes >= 0 ? 'Superávit' : 'Déficit'}</p>
            </div>
          </div>
        </div>

        {/* Boletos */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" /></svg>
            </div>
            <span className="text-white font-semibold">Boletos</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xl font-bold text-white">{lido ? boletosRegistered : '—'}</p>
              <p className="text-xs text-slate-500">Registrados</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">{lido ? boletosPaid : '—'}</p>
              {/* R5: conta as faturas da COMPETÊNCIA pagas dentro do mês. Pago em outubro de competência setembro não entra:
                  contar pelo mês do pagamento precisa de leitura que o backend ainda não dá. O rótulo diz a base. */}
              <p className="text-xs text-slate-500" title="Faturas desta competência pagas por boleto dentro do mês">Pagos no mês (da competência)</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">{lido ? boletosPending : '—'}</p>
              <p className="text-xs text-slate-500">Pendentes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Top 5 Inadimplentes + Top 5 Categorias */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top 5 Inadimplentes */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-central-elevado rounded-lg">
              <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
            </div>
            <span className="text-white font-semibold">Top 5 Inadimplentes</span>
          </div>
          {!lido ? (
            <p className="text-slate-400 text-sm text-center py-8" data-sem-leitura>Sem leitura deste período: não dá para dizer quem está vencido.</p>
          ) : topInadimplentes.length === 0 ? (
            <div className="text-center py-8">
              <div className="p-4 bg-slate-700/50 rounded-full inline-block mb-3">
                <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <p className="text-slate-400 font-medium">Nenhuma fatura vencida</p>
              <p className="text-slate-500 text-sm mt-1">Todas as faturas estão em dia</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topInadimplentes.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <span className="text-slate-500 text-sm font-mono w-5 text-right">{idx + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-medium truncate">{item.name}</span>
                      <div className="flex items-center gap-3 ml-2">
                        <span className="text-slate-500 text-xs">{item.count} fatura{item.count > 1 ? 's' : ''}</span>
                        <span className="text-white font-semibold text-sm">R$ {fmt(item.total)}</span>
                      </div>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div
                        className="bg-slate-400 h-1.5 rounded-full transition-all"
                        style={{ width: `${(item.total / maxInadimplente) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top 5 Categorias de Despesas */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-central-elevado rounded-lg">
                <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
              </div>
              <span className="text-white font-semibold">Top 5 Categorias</span>
            </div>
            {onNavigate && (
              <button onClick={() => onNavigate('despesas')} className="text-xs text-slate-300 hover:text-white hover:underline font-medium">
                Ver Despesas →
              </button>
            )}
          </div>
          {!lido ? (
            <p className="text-slate-400 text-sm text-center py-8" data-sem-leitura>Sem leitura deste período.</p>
          ) : topCategories.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-400 font-medium">Nenhuma despesa no mês</p>
              <p className="text-slate-500 text-sm mt-1">As categorias aparecerão aqui</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topCategories.map((cat, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-medium truncate">{cat.name}</span>
                      <span className="text-white font-semibold text-sm ml-2">R$ {fmt(cat.total)}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full transition-all"
                        style={{ width: `${(cat.total / maxCategoryTotal) * 100}%`, backgroundColor: cat.color }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {segmentErro && (
        <div role="alert" className={`px-4 py-3 rounded-xl border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-segmento="nao-carregou">
          Faturamento por segmento: não consegui carregar ({segmentErro}). O resto do painel segue valendo.
        </div>
      )}
      {/* Faturamento por Segmento de Cliente (competência, período selecionado) */}
      {segmentData && segmentData.segments.length > 0 && (() => {
        const maxTotal = Math.max(...segmentData.segments.map((s) => s.total), 1);
        const SEG_COLORS: Record<string, string> = {
          'Administração Pública': 'bg-blue-500', 'Supermercados / Alimentício': 'bg-emerald-500',
          'Serviços Profissionais': 'bg-purple-500', 'Saúde': 'bg-rose-500', 'TI / Software': 'bg-cyan-500',
          'Comércio / Varejo': 'bg-amber-500', 'Segurança Eletrônica': 'bg-red-500', 'Financeiro / Seguros': 'bg-teal-500',
          'Educação': 'bg-indigo-500', 'Construção / Imobiliário': 'bg-orange-500', 'Indústria / Fabricação': 'bg-lime-500',
        };
        return (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-central-elevado rounded-lg">
                  <svg className="w-5 h-5 text-central-texto-fraco" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5a4 4 0 11-8 0 4 4 0 018 0zm6 3a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                </div>
                <span className="text-white font-semibold">Faturamento por Segmento de Cliente</span>
              </div>
              <span className="text-slate-500 text-xs">Total R$ {fmt(segmentData.total)}</span>
            </div>
            <div className="space-y-2.5">
              {segmentData.segments.map((s) => {
                const pct = segmentData.total > 0 ? (s.total / segmentData.total) * 100 : 0;
                return (
                  <div key={s.segment} className="flex items-center gap-3">
                    <span className="text-slate-300 text-sm w-52 shrink-0 truncate" title={s.segment}>{s.segment}</span>
                    <div className="flex-1 bg-slate-700/50 rounded-full h-4 overflow-hidden">
                      <div className={`h-full ${SEG_COLORS[s.segment] || 'bg-slate-500'} rounded-full`} style={{ width: `${(s.total / maxTotal) * 100}%` }} />
                    </div>
                    <span className="text-slate-500 text-xs w-10 text-right shrink-0">{pct.toFixed(0)}%</span>
                    <span className="text-white text-sm font-semibold w-28 text-right shrink-0">R$ {fmt(s.total)}</span>
                    <span className="text-slate-500 text-xs w-20 text-right shrink-0 hidden lg:inline">{s.clients} cli.</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FinancialDashboardTab;
